import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight, ChevronLeft, Info, ArrowRight, AlertTriangle } from "lucide-react";
import { StatusDot } from "@/components/data/StatusDot";
import { EquipmentIconRow } from "@/components/mimic/EquipmentIcons";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const KIND_STYLE = {
  recycle: { color: "var(--oim-green)" },
  waste: { color: "#8B5CF6" },
};

const OVERLAY_KEYS = ["yield", "energy", "safety", "quality"];

const STATE_RANK = { normal: 0, watch: 1, warning: 2, high: 3, critical: 4 };
const rankToColor = (rank) => (rank === 0 ? "var(--oim-green)" : rank <= 2 ? "#FFB300" : "#FF3B30");

function StageDetailModal({ chainKey, stage, open, onOpenChange }) {
  const navigate = useNavigate();
  if (!stage) return null;
  const hasTwin = !!stage.twin_id;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="border max-w-md"
        style={{ backgroundColor: "var(--oim-surface)", borderColor: "var(--oim-border)" }}
        data-testid={`mimic-stage-modal-${chainKey}-${stage.code}`}
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-[var(--oim-text)] flex items-center gap-2">
            <span className="inline-block w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: stage.color }} />
            {stage.label}
          </DialogTitle>
          <DialogDescription className="sr-only">Process stage details for {stage.label}</DialogDescription>
        </DialogHeader>

        <div className="space-y-3 text-sm">
          <EquipmentIconRow icons={stage.icons} size={38} />
          {stage.equipment && (
            <div className="text-[11px] text-[var(--oim-text-dim)] leading-snug">{stage.equipment}</div>
          )}
          {stage.reaction && (
            <div className="text-xs font-mono-data" style={{ color: "var(--oim-gold)" }}>{stage.reaction}</div>
          )}
          {stage.items && (
            <div className="flex gap-1.5 flex-wrap">
              {stage.items.map((it) => (
                <span key={it} className="text-[10px] font-mono-data px-1.5 py-0.5 rounded border border-[var(--oim-border)] text-[var(--oim-text-muted)]">{it}</span>
              ))}
            </div>
          )}

          {stage.metrics && (
            <div className="grid grid-cols-2 gap-2 pt-1" data-testid={`mimic-stage-modal-metrics-${chainKey}-${stage.code}`}>
              {OVERLAY_KEYS.filter((k) => stage.metrics[k] != null).map((k) => (
                <div key={k} className="rounded-lg border px-2.5 py-1.5" style={{ borderColor: "var(--oim-border)" }}>
                  <div className="text-[9px] uppercase tracking-wider text-[var(--oim-text-dim)] font-heading">{k}</div>
                  <div className="font-mono-data text-sm font-semibold text-[var(--oim-text)]">
                    {stage.metrics[k]} <span className="text-[9px] text-[var(--oim-text-dim)] font-normal">{stage.metrics[`${k}_label`]}</span>
                  </div>
                </div>
              ))}
              {stage.metrics.state && (
                <div className="rounded-lg border px-2.5 py-1.5 flex items-center gap-1.5 col-span-2" style={{ borderColor: "var(--oim-border)" }}>
                  <StatusDot state={stage.metrics.state} showLabel />
                </div>
              )}
            </div>
          )}

          {stage.byproducts?.length > 0 && (
            <div>
              <div className="text-[9px] uppercase tracking-wider text-[var(--oim-text-dim)] font-heading mb-1">Byproducts / Streams</div>
              <div className="flex flex-wrap gap-1.5">
                {stage.byproducts.map((b, i) => (
                  <span
                    key={i}
                    className="text-[10px] px-1.5 py-0.5 rounded-full border leading-tight"
                    style={{ borderColor: KIND_STYLE[b.kind]?.color, color: KIND_STYLE[b.kind]?.color, borderStyle: "dashed" }}
                  >
                    {b.text}
                  </span>
                ))}
              </div>
            </div>
          )}

          {!hasTwin && (
            <div className="flex items-center gap-1.5 text-[10px] text-[var(--oim-text-dim)] pt-1">
              <Info className="w-3 h-3 shrink-0" /> No live digital twin instrumented for this node yet.
            </div>
          )}
        </div>

        {hasTwin && (
          <DialogFooter>
            <Button
              className="gap-1.5"
              style={{ backgroundColor: "var(--oim-green)", color: "#04140a" }}
              onClick={() => navigate(`/twins/${stage.twin_id}`)}
              data-testid={`mimic-stage-view-twin-${chainKey}-${stage.code}`}
            >
              View Full Twin Details <ArrowRight className="w-3.5 h-3.5" />
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

function StageCard({ chainKey, stage, overlay, onSelect }) {
  const metric = overlay === "safety" ? null : stage.metrics?.[overlay];
  const metricLabel = stage.metrics?.[`${overlay}_label`];
  const hasTwin = !!stage.twin_id;
  const faultRank = STATE_RANK[stage.metrics?.state] || 0;

  return (
    <div className="flex flex-col h-full w-[168px] lg:w-[188px] shrink-0" data-testid={`mimic-stage-${chainKey}-${stage.code}`}>
      <button
        type="button"
        onClick={() => onSelect(stage)}
        data-testid={`mimic-stage-link-${chainKey}-${stage.code}`}
        className="group flex-1 flex flex-col justify-between text-left rounded-lg border-t-[3px] border px-3 py-2.5 transition-all duration-150 cursor-pointer hover:-translate-y-0.5 hover:shadow-lg"
        style={{ borderTopColor: stage.color, borderColor: faultRank > 0 ? rankToColor(faultRank) : "var(--oim-border)", backgroundColor: "var(--oim-surface)" }}
      >
        <div>
          <div className="flex items-start justify-between gap-1">
            <div className="text-xs font-heading font-bold text-[var(--oim-text)] leading-tight">{stage.label}</div>
            {faultRank > 0 ? (
              <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" style={{ color: rankToColor(faultRank) }} data-testid={`mimic-stage-fault-icon-${chainKey}-${stage.code}`} />
            ) : hasTwin ? (
              <ChevronRight className="w-3.5 h-3.5 shrink-0 mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: "var(--oim-green)" }} />
            ) : (
              <Info className="w-3 h-3 shrink-0 mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: "var(--oim-text-dim)" }} />
            )}
          </div>
          <div className="mt-1.5"><EquipmentIconRow icons={stage.icons} size={24} /></div>
          {stage.equipment && <div className="text-[9px] text-[var(--oim-text-dim)] mt-1 leading-snug">{stage.equipment}</div>}
          {stage.reaction && <div className="text-[9.5px] font-mono-data mt-1.5" style={{ color: "var(--oim-gold)" }}>{stage.reaction}</div>}
          {stage.items && (
            <div className="flex gap-1.5 mt-1.5 flex-wrap">
              {stage.items.map((it) => (
                <span key={it} className="text-[9px] font-mono-data px-1.5 py-0.5 rounded border border-[var(--oim-border)] text-[var(--oim-text-muted)]">{it}</span>
              ))}
            </div>
          )}
        </div>
        <div className="mt-2">
          {overlay === "safety" ? (
            stage.metrics?.safety ? <StatusDot state={stage.metrics.safety} showLabel /> : (
              <div className="flex items-center gap-1.5">
                <StatusDot state={stage.metrics?.state} showLabel={false} />
                <span className="text-[9px] text-[var(--oim-text-dim)]">—</span>
              </div>
            )
          ) : metric != null ? (
            <div className="flex items-center gap-1.5">
              <StatusDot state={stage.metrics.state} showLabel={false} />
              <span className="font-mono-data text-sm font-semibold text-[var(--oim-text)]">{metric}</span>
              <span className="text-[9px] text-[var(--oim-text-dim)]">{metricLabel}</span>
            </div>
          ) : stage.metrics?.state ? (
            <div className="flex items-center gap-1.5">
              <StatusDot state={stage.metrics.state} showLabel={false} />
              <span className="text-[9px] text-[var(--oim-text-dim)]">—</span>
            </div>
          ) : <span className="text-[9px] text-[var(--oim-text-dim)]">&nbsp;</span>}
        </div>
      </button>
      <div className="min-h-[22px] flex flex-wrap gap-1 mt-1.5">
        {stage.byproducts?.map((b, i) => (
          <span
            key={i}
            className="text-[8.5px] px-1.5 py-0.5 rounded-full border leading-tight"
            style={{ borderColor: KIND_STYLE[b.kind]?.color, color: KIND_STYLE[b.kind]?.color, borderStyle: "dashed" }}
            data-testid={`mimic-byproduct-${chainKey}-${stage.code}-${i}`}
          >
            {b.text}
          </span>
        ))}
      </div>
    </div>
  );
}

function Connector({ color = "var(--oim-green)" }) {
  return (
    <div className="self-center shrink-0 pb-[26px]" data-testid="mimic-connector">
      <svg width="18" height="16">
        <line x1="0" y1="8" x2="12" y2="8" stroke={color} strokeWidth="2" className="flow-line" />
        <polygon points="11,3 11,13 18,8" fill={color} />
      </svg>
    </div>
  );
}

function ScrollRow({ testId, children }) {
  const scrollRef = useRef(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const update = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 4);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  };

  useEffect(() => {
    update();
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      el.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, []);

  const scrollBy = (dir) => scrollRef.current?.scrollBy({ left: dir * 240, behavior: "smooth" });

  return (
    <div className="relative">
      <div ref={scrollRef} className="flex items-stretch gap-2 overflow-x-auto pb-2 mimic-scroll" data-testid={testId}>
        {children}
      </div>
      {canLeft && (
        <button
          type="button"
          onClick={() => scrollBy(-1)}
          data-testid={`${testId}-scroll-left`}
          aria-label="Scroll left"
          className="absolute left-0 top-0 bottom-2 flex items-center pl-0.5 pr-3 z-10"
          style={{ background: "linear-gradient(to right, var(--oim-bg), transparent)" }}
        >
          <ChevronLeft className="w-4 h-4" style={{ color: "var(--oim-green)" }} />
        </button>
      )}
      {canRight && (
        <button
          type="button"
          onClick={() => scrollBy(1)}
          data-testid={`${testId}-scroll-right`}
          aria-label="Scroll right"
          className="absolute right-0 top-0 bottom-2 flex items-center pr-0.5 pl-3 z-10"
          style={{ background: "linear-gradient(to left, var(--oim-bg), transparent)" }}
        >
          <ChevronRight className="w-4 h-4" style={{ color: "var(--oim-green)" }} />
        </button>
      )}
    </div>
  );
}

export function FlowDiagram({ chainKey, flow }) {
  const [selected, setSelected] = useState(null);
  if (!flow) return null;
  let runningRank = 0;
  return (
    <div className="space-y-2" data-testid={`mimic-flow-${chainKey}`}>
      <div className="text-[10px] uppercase tracking-widest text-[var(--oim-text-dim)] font-heading">{flow.label}</div>
      <ScrollRow testId={`mimic-flow-scroll-${chainKey}`}>
        {flow.stages.map((stage, idx) => {
          runningRank = Math.max(runningRank, STATE_RANK[stage.metrics?.state] || 0);
          const connectorColor = rankToColor(runningRank);
          return (
            <div key={stage.code} className="flex items-stretch gap-2">
              <StageCard chainKey={chainKey} stage={stage} overlay={flow.overlay} onSelect={setSelected} />
              {idx < flow.stages.length - 1 && <Connector color={connectorColor} />}
            </div>
          );
        })}
        <Connector color={rankToColor(runningRank)} />
        <div
          className="flex flex-col h-full w-[168px] lg:w-[184px] shrink-0 rounded-lg border px-3 py-2.5 justify-center"
          style={{ borderColor: "var(--oim-border)", backgroundColor: "var(--oim-surface-soft)" }}
          data-testid={`mimic-end-products-${chainKey}`}
        >
          <div className="text-[9px] uppercase tracking-widest text-[var(--oim-text-dim)] font-heading mb-1">End Products / Markets</div>
          {flow.end_products.map((p) => (
            <div key={p} className="text-[10px] text-[var(--oim-text-secondary)] leading-relaxed">{p}</div>
          ))}
        </div>
      </ScrollRow>
      <StageDetailModal chainKey={chainKey} stage={selected} open={!!selected} onOpenChange={(o) => !o && setSelected(null)} />
    </div>
  );
}
